import { Component, Input, computed, effect, signal } from '@angular/core';
import { PAGE_LABEL_MAT_PAGINATOR_INTL_PROVIDER } from 'src/app/shared/providers';
import {
  DeviceEvent,
  SeverityLevel,
} from '../../unusual-network-metrics.model';
import type { Device } from './unm-data-table.component.types';

@Component({
  selector: 'app-unm-data-table',
  templateUrl: './unm-data-table.component.html',
  providers: PAGE_LABEL_MAT_PAGINATOR_INTL_PROVIDER,
  standalone: false,
})
export class UnmDataTableComponent {
  @Input() isNoContent = false;

  @Input() isLoading = false;

  @Input({ required: true }) set deviceEvents(value: DeviceEvent[]) {
    this.events$.set(
      value?.sort((a, b) => {
        const dateA = new Date(`${a.date} ${a.begin_time}`).getTime();
        const dateB = new Date(`${b.date} ${b.begin_time}`).getTime();
        return dateB - dateA;
      }) ?? [],
    );
  }

  private readonly events$ = signal<DeviceEvent[]>([]);

  private readonly devices$ = computed(() => {
    const lookup: Record<string, DeviceEvent[]> = {};
    const events = this.events$();

    events.forEach((event) => {
      event.begin_time = this.convertToNormalTime(event.begin_time);
      event.end_time = this.convertToNormalTime(event.end_time);
      (lookup[event.device_name] ??= []).push(event);
    });

    return Object.entries(lookup).map(([deviceName, deviceEvents]) => ({
      events: deviceEvents,
      deviceName,
    }));
  });

  readonly selectedDevice$ = signal<Device | null>(null);

  readonly filteredDevices$ = computed(() => {
    const searchTerm = this.searchTerm$();
    const severityLevels = Object.entries(this.severityCheckboxes)
      .filter(([_, checked]) => checked())
      .map(([key]) => key as SeverityLevel);

    let filteredDevices = this.devices$();

    // Filter by search term
    if (searchTerm) {
      filteredDevices = filteredDevices.filter((device) =>
        device.deviceName.toLowerCase().includes(searchTerm.toLowerCase()),
      );
    }

    if (severityLevels.length > 0) {
      filteredDevices = filteredDevices.filter((device) =>
        device.events.some((event) =>
          severityLevels.includes(event.action_label),
        ),
      );
    } else {
      filteredDevices = [];
    }

    return filteredDevices;
  });

  readonly filteredSelectedDeviceEvents$ = computed(() => {
    const events = this.selectedDevice$()?.events ?? [];
    const severityLevels = Object.entries(this.severityCheckboxes)
      .filter(([_, checked]) => checked())
      .map(([key]) => key as SeverityLevel);

    this.pageNumber = 0;
    return events.filter((event) =>
      severityLevels.includes(event.action_label),
    );
  });

  pageNumber = 0;

  readonly SeverityLevel = SeverityLevel;

  readonly searchTerm$ = signal<string>('');

  readonly severityCheckboxes = Object.freeze(
    Object.fromEntries(
      Object.values(SeverityLevel).map((key) => [key, signal(true)]),
    ),
  );

  constructor() {
    // Clears selection if it no longer exists in filtered list
    effect(() => {
      const selectedDevice = this.selectedDevice$();
      const filteredDevices = this.filteredDevices$();

      if (
        selectedDevice &&
        !filteredDevices.some(
          (device) => device.deviceName === selectedDevice.deviceName,
        )
      ) {
        this.selectedDevice$.set(null);
      }
    });

    // On initial load, select the first device (and when selection is cleared)
    effect(() => {
      const selectedDevice = this.selectedDevice$();
      const filteredDevices = this.filteredDevices$();

      if (!selectedDevice && filteredDevices.length > 0) {
        this.selectedDevice$.set(filteredDevices[0]);
      }
    });
  }

  private convertToNormalTime(militaryTime: string): string {
    if (militaryTime.includes('AM') || militaryTime.includes('PM')) {
      return militaryTime;
    }

    const [hoursRaw, minutesRaw] = militaryTime.split(':');
    const hours = Number(hoursRaw);
    const minutes = Number(minutesRaw);
    const period = hours >= 12 ? 'PM' : 'AM';
    const normalizedHours = hours % 12 || 12;

    return `${normalizedHours}:${minutes.toString().padStart(2, '0')} ${period}`;
  }
}
